import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const GP_SYNC_DELAY_MS = 200;
const GP_PENDING_TTL_MS = 8000;
const gpPendingPhoto = new WeakMap();

function gpPhotoColour(alt) {
  const match = typeof alt === 'string' ? alt.match(/\s+in\s+(.+?)\s+\d+$/i) : null;
  return match?.[1]?.trim() || null;
}

function gpRestorePhoto(gallery, alt) {
  let frames = 0;
  let stable = 0;
  const step = () => {
    if (!gallery.isConnected || ++frames > 60) return;
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const index = slides.findIndex((slide) => slide.querySelector('img')?.getAttribute('alt') === alt);
    if (index < 0) return;
    if (gallery.slideshow?.current === index) stable += 1;
    else {
      stable = 0;
      gallery.slideshow?.select(index, undefined, { animate: false });
    }
    if (stable < 3) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleGpSlide, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    clearTimeout(this.#gpSyncTimer);
  }

  #gpSyncTimer;

  #handleGpSlide = (event) => {
    if (event.detail?.userInitiated !== true || event.detail.trigger !== 'select') return;
    const alt = event.detail.slide?.querySelector('img')?.getAttribute('alt');
    const colour = gpPhotoColour(alt);
    if (!colour) return;
    clearTimeout(this.#gpSyncTimer);
    this.#gpSyncTimer = setTimeout(() => {
      if (!this.isConnected) return;
      const scope = this.closest('.shopify-section, dialog');
      const picker = scope?.querySelector('variant-picker');
      if (!picker) return;
      const radios = Array.from(picker.querySelectorAll('fieldset input[type="radio"]')).filter(
        (radio) => radio instanceof HTMLInputElement && /^colou?r$/i.test(radio.dataset.optionName ?? '')
      );
      if (radios.some((radio) => radio.checked && radio.value === colour)) return;
      const matches = radios.filter((radio) => radio.value === colour);
      if (matches.length !== 1 || matches[0].disabled) return;
      gpPendingPhoto.set(scope, { alt, at: Date.now() });
      matches[0].click();
    }, GP_SYNC_DELAY_MS);
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const scope = this.closest('.shopify-section, dialog');

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        const pending = scope ? gpPendingPhoto.get(scope) : undefined;
        if (pending) {
          gpPendingPhoto.delete(scope);
          if (Date.now() - pending.at <= GP_PENDING_TTL_MS) gpRestorePhoto(newMediaGallery, pending.alt);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
